<template>
    <div class="page">
        <div class="content">
            <ContentBlock bgColor="#FFFFFF" id="topBlock">
                    <h3 class="topTitle">Brand &amp; Company Identity</h3>
                    <img class="logo" :src="require(`@/assets/1-EVOLVE_black_logo.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Laying the Foundation</h4>
                    <p>When I joined EVOLVE in 2021, the company was still an emerging startup operating under Applied Software and had not yet established a distinct brand identity. In April 2022, EVOLVE became an independent company, marking the beginning of a period of rapid growth and expansion.</p>
                    <img :src="require(`@/assets/2-Laying_the_Foundation.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h3 class="white-text">Website Redesign</h3>
                    <h4>Framer: Design, Publish, Done</h4>
                    <p class="white-text">Using Framer software, my colleague and I redesigned and rebuilt the EVOLVE website from the ground up, transitioning it from an outsourced platform to an internally managed solution. This shift reduced annual costs by nearly $100,000 while giving the UX Design team direct ownership of the website, marketing content, brand presentation, and overall company voice.<br><br>Managing the website in-house significantly increased our team's agility. New landing pages could be designed, built, and published within hours instead of days or weeks, and product downloads, announcements, and marketing materials could be made available in minutes. This faster workflow enabled leadership to respond quickly to business priorities, launch campaigns more efficiently, and deliver timely information to both existing and prospective customers.</p>
                    <img :src="require(`@/assets/3-Framer_Design_Publish_Done.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h4>Design Systems</h4>
                    <p class="white-text"><strong class="uppercase">Framer</strong><br>We developed a comprehensive design system in Framer to support the company website and broader brand identity. This system included reusable components, standardized typography, and a cohesive visual language to ensure consistency across all digital experiences.</p>
                    <img :src="require(`@/assets/4-Design_Systems_Framer.png`)" />
                    <p class="white-text"><strong class="uppercase">Figma</strong><br>We consolidated the typography, styling, and components developed for EVOLVE Shop and EVOLVE Fabrication into a unified design system in Figma. This system included a range of reusable UI elements such as status indicators, Kanban cards, forms, and navigation components, establishing a consistent and cohesive design language across EVOLVE products.</p>
                    <img :src="require(`@/assets/5-Design_Systems_Figma.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h3>Brand Identity</h3>
                    <h4>Product Logo Designs</h4>
                    <p>When I joined EVOLVE, the company offered two core products: EVOLVE Electrical and EVOLVE Mechanical. Since 2021, the product portfolio has expanded significantly, growing to include more than five additional solutions. As the suite evolved, leadership identified the need to establish a more distinct and scalable brand system.<br><br>In collaboration with the CEO and Director of Marketing, I led the development of unique visual identities for each product while ensuring alignment with the overarching EVOLVE brand. Building on the existing EVOLVE logo, I created a cohesive system of product identities that gave each solution a clear and recognizable presence, while maintaining consistency across the broader brand ecosystem. This initiative helped position the expanding portfolio as a unified yet differentiated suite of products.</p>
                    <img :src="require(`@/assets/6-Product_Logo_Designs.png`)" />
                    <img :src="require(`@/assets/7-vdcOS_logos.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Marketing Event Booth</h4>
                    <p>The marketing team required professional, visually engaging backdrops for use at events. In response, our UX Design team curated a selection of global customers actively using our products to feature across event materials and website assets, strengthening both visual impact and credibility.</p>
                    <img :src="require(`@/assets/8-Marketing_event_booth_designs.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>Social Media</h4>
                    <p>Leadership aimed to establish greater consistency across all brand touchpoints, including in-app messaging, email communications, external newsletters, and social media content. To support this effort, we redefined the brand system and phased out outdated visual elements, creating a refreshed and more cohesive identity for the company.</p>
                    <img :src="require(`@/assets/9-Social_Media.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>The Orange Print</h4>
                    <p>I was a founding member of <strong><i>The Orange Print</i></strong>, an internal initiative established to strengthen company culture and improve cross-departmental morale. My primary focus was contributing to the content strategy and design of the EVOLVE internal newsletter, which highlights company milestones, key achievements, and employee spotlights recognizing individuals who go above and beyond.<br><br>In addition, we introduced bi-annual fitness challenges and team-building events designed to foster stronger connections across departments and encourage collaboration among employees who may not regularly interact in their day-to-day work.</p>
                    <img :src="require(`@/assets/10-The_Orange_Print.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#0E0C0A" class="project-text">
                    <h3 class="white-text">Brand Guidelines</h3>
                    <h4>The Making of a Brand</h4>
                    <p class="white-text">We streamlined the existing brand system by removing outdated and unnecessary elements while introducing refined additions to strengthen consistency. These updates enhanced the overall visual identity across the website and marketing materials, creating a more cohesive and modern brand presence.</p>
                    <img :src="require(`@/assets/11-Brand_Guidelines.png`)" />
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h3>The Reflection</h3>
                    <h4>Impact on the Company</h4>
                    <p>The initiative focused on defining and refining the EVOLVE brand by evaluating existing elements—identifying what was effective, what was inconsistent, and where improvements were needed. This process helped establish a clear direction for the desired visual language, tone, and overall brand experience, ensuring a cohesive look and feel that aligned with the company’s vision.<br><br>The objective was to position EVOLVE as a professional, differentiated brand within a competitive market while also reinforcing a strong internal culture. By aligning external perception with internal values, the brand was designed to support both market credibility and long-term organizational health and growth from the outset.</p>
            </ContentBlock>
            <ContentBlock bgColor="#FFFFFF" class="project-text">
                    <h4>What I Learned</h4>
                    <p>I value having a meaningful influence on company culture, as I have seen firsthand the challenges that arise when it is not intentionally developed. A company is shaped not only by the products it creates, but also by its people and leadership.<br><br>Through design and content, I have learned that I can play an active role in shaping that culture across multiple areas of the organization. At EVOLVE, where the company remains relatively small with approximately 70 employees, there has been significant opportunity to contribute directly and leave a lasting impact.</p>
                    <img class="logo" id="bottomLogo" :src="require(`@/assets/1-EVOLVE_black_logo.png`)" />
            </ContentBlock>
        </div>
        <Scroll />
    </div>
</template>

<script>
    import ContentBlock from '../components/ContentBlock.vue';
    import Scroll from "../components/Scroll.vue";

    export default {
        name: 'Shop',
        components: {
            ContentBlock,
            Scroll
        }
    }
</script>

<style scoped>
    .intro{
        text-align: center;
        font-weight: 700;
        margin-top: 50px;
        padding: 0px 30px;
       box-sizing: border-box;
    }

    #topBlock :deep(.contentBlock) {
        margin-bottom: 0;
    }

    #bottomLogo{
        max-width: 200px;
        margin-top: 50px;
    }

     h1{
        font-size: 3em;
        font-weight: 500;
    }

    h2{
        font-size: 2em;
        margin-top: 10px;
    }

    h3, h5{
        color: #0E0C0A;
        text-transform: uppercase;
    }

    h3{
        font-size: 1.3em;
    }

    h4{
       color: #C5B9A6; 
       font-size: 2em;
    }

    .white-text{
        color: #FFFFFF !important;
    }

    .topTitle{
       padding: 50px;
    }

    .uppercase{
        text-transform: uppercase;
    }

    .content{
        margin: 100px 0px 30px 0px;
    }

    img.logo{
        max-width: 400px;
    }

    .project-text{
        padding: 50px;
    }

    video{
        width: 100%;
    }

    @media only screen and (max-width: 700px){
            h1{
                font-size: 2em;
            }

            h2{
                font-size: 1.3em;
                margin-top: 5px;
            }

            .project-text{
                padding: 30px;
            }
    }
</style>
